// DP terbayar per pesanan yang BELUM tercermin sebagai pcs lunas — bahan
// potongan "total belum lunas" di kotak ringkasan keranjang pembeli (/orders),
// admin (Keranjang Pelanggan), dan "Nominal belum lunas" kartu reseller board
// /admin/orders. Bughunt E7-1, putusan pemilik 2026-09-30: DP dipotong dari
// total belum lunas. Rumusnya ada di `cartDpCreditByOrder` (src/lib/orderCart.ts,
// ber-tes); berkas ini hanya mengambil bahannya.
//
// Query-nya kecil dan HANYA untuk pesanan yang nota DP-nya lunas — pembeli
// tanpa DP lunas (kebanyakan) tak membayar round-trip apa pun. `.in()` dipecah
// per 150 id (≈ 5,5 KB query string, idiom `fetchDpHangusOrderIds` di board).
// Galat baca → peta kosong = tanpa potongan (perilaku sebelum E7-1): DP yang
// Kredit DP-nya tak terbaca TIDAK boleh dipotong, karena kredit yang sudah
// terpasang sudah menutup pcs dan memotongnya lagi membuat totalnya terlalu kecil.
//
// `fetchCartPaidResidual` (audit pemilik 2026-10-24) membaca bahan kedua: uang
// yang SUDAH masuk ke nota barang open/partial tapi belum genap satu pcs —
// Kredit DP yang belum menutup pcs (dikembalikan ke DP di atas) dan bayar
// sebagian (dipotong per item). Rumusnya `cartPaidResidual`
// (src/lib/cartPaidResidual.ts, ber-tes, dicocokkan ke SQL PROD).

import type { createBrowserSupabase } from "@/lib/supabase/browser";
import {
  cartPaidResidual,
  type CartPaidResidual,
  type ResidualInvoiceLike
} from "@/lib/cartPaidResidual";
import {
  cartDpCreditByOrder,
  DP_CREDIT_REFLECTED_STATUSES,
  type DpCreditLineLike,
  type DpPaidRowLike
} from "@/lib/orderCart";

type Db = NonNullable<ReturnType<typeof createBrowserSupabase>>;

const CHUNK = 150;

function chunks<T>(xs: readonly T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += CHUNK) out.push(xs.slice(i, i + CHUNK));
  return out;
}

/**
 * @param dpRows nota DP yang SUDAH dibaca pemanggil beserta buku penerimaannya
 *   (`amount_paid`, `balance_applied`, `refunded_amount`).
 * @param unconvertedByOrder Kredit DP yang belum jadi pcs lunas
 *   (`fetchCartPaidResidual(...).dpByOrder`). Absen = perilaku lama.
 */
export async function fetchCartDpCredit(
  db: Db,
  dpRows: ReadonlyArray<DpPaidRowLike>,
  unconvertedByOrder?: ReadonlyMap<string, number> | null
): Promise<Map<string, number>> {
  const orderIds = [
    ...new Set(
      dpRows
        .filter((r) => r.status === "paid" && r.source_order_id)
        .map((r) => r.source_order_id as string)
    )
  ];
  if (!orderIds.length) return new Map();

  const lines: DpCreditLineLike[] = [];
  for (const ids of chunks(orderIds)) {
    const { data, error } = await db
      .from("invoice_lines")
      .select("order_id, amount, invoices!inner(status)")
      .eq("kind", "dp_credit")
      .in("order_id", ids)
      .is("cancelled_at", null)
      .in("invoices.status", [...DP_CREDIT_REFLECTED_STATUSES]);
    if (error) return new Map();
    lines.push(...((data ?? []) as any[]));
  }
  return cartDpCreditByOrder(dpRows, lines, unconvertedByOrder);
}

/**
 * Varian untuk pemanggil yang baru tahu id pesanannya (board /admin/orders:
 * RPC `admin_cart_board` hanya membawa STATUS nota DP aktif, bukan nominal).
 * Membaca nota DP `paid` pesanan-pesanan itu lalu `fetchCartDpCredit`.
 */
export async function fetchCartDpCreditForOrders(
  db: Db,
  orderIds: ReadonlyArray<string>,
  unconvertedByOrder?: ReadonlyMap<string, number> | null
): Promise<Map<string, number>> {
  const ids = [...new Set(orderIds.filter(Boolean))];
  if (!ids.length) return new Map();

  const dpRows: DpPaidRowLike[] = [];
  for (const chunk of chunks(ids)) {
    const { data, error } = await db
      .from("invoices")
      .select("status, source_order_id, amount_paid, balance_applied, refunded_amount")
      .eq("kind", "dp")
      .eq("status", "paid")
      .in("source_order_id", chunk);
    if (error) return new Map();
    dpRows.push(...((data ?? []) as any[]));
  }
  return fetchCartDpCredit(db, dpRows, unconvertedByOrder);
}

const EMPTY_RESIDUAL = (): CartPaidResidual => ({
  dpByOrder: new Map(),
  moneyByItem: new Map()
});

/**
 * Uang yang sudah masuk ke nota barang pelanggan-pelanggan ini tapi belum
 * genap satu pcs (`cartPaidResidual`).
 *
 * Hanya dua jenis nota yang bisa menyimpannya, jadi hanya itu yang dibaca:
 * nota `partial` (PROD 2026-10-24: 508 nota / 2.143 baris) dan nota `open`
 * yang memuat Kredit DP (8 nota) — nota open lainnya belum bersentuhan uang.
 * Galat baca → tanpa potongan (perilaku lama): sisa yang tak terbaca tak
 * boleh ditebak, karena potongan yang salah membuat tagihan terlihat lebih
 * kecil dari yang harus dibayar.
 */
export async function fetchCartPaidResidual(
  db: Db,
  customerIds: ReadonlyArray<string>
): Promise<CartPaidResidual> {
  const ids = [...new Set(customerIds.filter(Boolean))];
  if (!ids.length) return EMPTY_RESIDUAL();
  try {
    const invoices: ResidualInvoiceLike[] = [];
    const openWithCredit = new Set<string>();
    await Promise.all(
      chunks(ids).map(async (chunk) => {
        const [partial, credit] = await Promise.all([
          db
            .from("invoices")
            .select(
              "id, status, amount_paid, balance_applied, refunded_amount, invoice_lines(id, kind, order_item_id, order_id, qty, amount, paid_qty, created_at, cancelled_at)"
            )
            .eq("status", "partial")
            .neq("kind", "dp")
            .in("customer_id", chunk),
          db
            .from("invoice_lines")
            .select("invoice_id, invoices!inner(status, kind, customer_id)")
            .eq("kind", "dp_credit")
            .is("cancelled_at", null)
            .eq("invoices.status", "open")
            .in("invoices.customer_id", chunk)
        ]);
        if (partial.error) throw partial.error;
        if (credit.error) throw credit.error;
        invoices.push(...((partial.data ?? []) as any[]));
        for (const r of (credit.data ?? []) as any[]) {
          if (r.invoice_id) openWithCredit.add(r.invoice_id as string);
        }
      })
    );
    for (const chunk of chunks([...openWithCredit])) {
      const { data, error } = await db
        .from("invoices")
        .select(
          "id, status, amount_paid, balance_applied, refunded_amount, invoice_lines(id, kind, order_item_id, order_id, qty, amount, paid_qty, created_at, cancelled_at)"
        )
        .in("id", chunk)
        .eq("status", "open");
      if (error) throw error;
      invoices.push(...((data ?? []) as any[]));
    }
    return cartPaidResidual(invoices);
  } catch {
    return EMPTY_RESIDUAL();
  }
}
